import { defineComponent as _defineComponent } from 'vue'
import BreadCrumbComponent from "@/components/bread-crumb-component.vue";
import { ref, Ref, onMounted } from "vue";
import useHttpRequest from "@/composables/use-http-request";
import AwsUploadUrl from "@/interfaces/awsUploadUrl.interface";
import { useShowWarningMessage } from "@/composables/use-notifications";
import axios from "axios";
import store from "@/store";
import { useRoute, useRouter } from "vue-router/composables";
import CourseWork from "@/interfaces/courseWork.interface";
import getMonthName from "@/helpers/getMonthName";
import { Loading } from "element-ui";
import { ElInputFile } from "@/interfaces/elInputFie.interface";

interface IntakeGRoupSearchResult {
  course_intake_batch_group_id: string;
  label: string;
}

interface ModuleSearchResult {
  course_unit_intake_batch_combined_id: string;
  course_unit_with_intake: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'edit-course-work',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const isSearching = ref(false);

const totalMarks = ref("");
const courseworkTitle = ref("");
const courseworkInstructions = ref("");
const startTime = ref("");
const endTime = ref("");
const isFileUploadAllowed = ref(false);

const chosenCourseUnitsWithIntakes = ref([] as string[]);
const chosenIntakeGroups = ref([] as string[]);
const courseUnitsWithCourseIntakeBatches = ref<ModuleSearchResult[]>([]);
const intakeGRoupSearchResults = ref<IntakeGRoupSearchResult[]>([]);
const courseworkQuestionFiles = ref<ElInputFile[]>([]);
const answerSheetsFiles = ref<ElInputFile[]>([]);
const selectedAnswerFile: Ref<ElInputFile | null> = ref(null);
const selectedQuestionFile: Ref<ElInputFile | null> = ref(null);

// const hasNo

const getCourseWork = async () => {
  const request = await useHttpRequest<{
    success: boolean;
    message: string;
    course_work: CourseWork;
  }>({
    method: "GET",
    url: `course-work/fetch-one/${route.params.courseWorkId}`,
    loadingProperty: null,
    showFullScreenLoader: true,
    showSuccessMessage: false,
  });

  if (!request) {
    return router.back();
  }

  if (
    request.success &&
    request.message == "COURSE_WORK FETCHED SUCCESSFULLY"
  ) {
    const courseWork = request.course_work;
    courseworkTitle.value = courseWork.title;
    totalMarks.value = courseWork.total_marks?.toString() ?? "";
    // for_marks_only.value = courseWork.for_marks_only;
    startTime.value = courseWork.available_from;

    endTime.value = courseWork.available_upto;
    courseworkInstructions.value = courseWork.instructions;

    const preparedCourseUnitsWithBatches: ModuleSearchResult[] = [];
    const preChosenCourseUnitsWithBatches: string[] = [];

    courseWork.course_work_intakes.forEach((course_work_intake) => {
      preChosenCourseUnitsWithBatches.push(
        `${course_work_intake.course_intake_batch_id}_${course_work_intake.course_unit_id}`
      );
      preparedCourseUnitsWithBatches.push({
        course_unit_intake_batch_combined_id: `${course_work_intake.course_intake_batch_id}_${course_work_intake.course_unit_id}`,
        course_unit_with_intake: `${
          course_work_intake.course_unit.course_unit_code
        } (${course_work_intake.course_unit.title}) in ${
          course_work_intake.course_intake_batch.course.course_code
        } ${getMonthName(
          course_work_intake.course_intake_batch.month
        ).toUpperCase()}/${course_work_intake.course_intake_batch.year} ${
          course_work_intake.course_intake_batch.cohort
        }`,
      });
    });

    courseUnitsWithCourseIntakeBatches.value = preparedCourseUnitsWithBatches;
    chosenCourseUnitsWithIntakes.value = preChosenCourseUnitsWithBatches;

    courseworkQuestionFiles.value = [
      {
        name: "Question Paper",
      },
    ];

    if (courseWork.answer_sheet_path_key) {
      answerSheetsFiles.value = [
        {
          name: "Answer Sheet",
        },
      ];
    }

    // populate intakeGroup dropdown
    intakeGRoupSearchResults.value = courseWork.course_intake_batch_groups.map(
      (group) => {
        return {
          course_intake_batch_group_id: group.course_intake_batch_group_id,
          label: `${group.course_intake_batch.course.course_code} 
                  ${getMonthName(
                    group.course_intake_batch.month
                  ).toUpperCase()}/${group.course_intake_batch.year} ${
            group.course_intake_batch.cohort
          } ${group.title}`,
        };
      }
    );

    // preselect chosenIntakeGroups
    chosenIntakeGroups.value = courseWork.course_intake_batch_groups.map(
      (group) => group.course_intake_batch_group_id
    );

    isFileUploadAllowed.value = courseWork.is_answersheet_file_upload_allowed;
  } else {
    router.back();
  }
};

onMounted(() => {
  if (route.params.courseWorkId) {
    getCourseWork();
  } else {
    router.back();
  }
});

const onSearchModules = async (query: string) => {
  if (query !== "" && query.length >= 3) {
    const request = await useHttpRequest<{
      success: boolean;
      message: string;
      search_results: ModuleSearchResult[];
    }>({
      method: "GET",
      url: `course-units/search-course-unit?search_query=${query}&lecturer_id=${store.state.userId}`,
      loadingProperty: isSearching,
      showSuccessMessage: false,
    });

    if (!request) return;

    if (
      request.success &&
      request.message == "SEARCH RESULTS RETRIEVED SUCCESSFULLY"
    ) {
      courseUnitsWithCourseIntakeBatches.value = request.search_results;
    }
  }
};

const onSearchIntakeGroups = async (query: string) => {
  if (!chosenCourseUnitsWithIntakes.value.length) {
    return useShowWarningMessage(
      "Modules Required",
      "Please select at least one module before searching for intake groups"
    );
  }
  if (query !== "" && query.length >= 3) {
    const request = await useHttpRequest<{
      success: boolean;
      message: string;
      results: IntakeGRoupSearchResult[];
    }>({
      method: "POST",
      url: `course-intake-batches/intake-groups/search?query=${query}`,
      loadingProperty: isSearching,
      showSuccessMessage: false,
      body: {
        course_intake_batch_ids: chosenCourseUnitsWithIntakes.value.map(
          (item) => item.split("_")[0]
        ),
      },
    });

    if (!request) return;

    if (
      request.success &&
      request.message == "Intake groups retrieved successfully"
    ) {
      intakeGRoupSearchResults.value = request.results;
    }
  }
};

const handleQuestionPreview = (file: ElInputFile) => {
  courseworkQuestionFiles.value = [file];
  selectedQuestionFile.value = file;
};

const handleQuestionRemove = () => {
  selectedQuestionFile.value = null;
  courseworkQuestionFiles.value = [];
};

const handleAnswerPreview = (file: ElInputFile) => {
  answerSheetsFiles.value = [file];
  selectedAnswerFile.value = file;
};

const handleAnswerRemove = () => {
  selectedAnswerFile.value = null;
  answerSheetsFiles.value = [];
};

const requestUploadUrls = async ({
  shouldUploadQuestionPaper,
  shouldUploadAnswerSheet,
}: {
  shouldUploadQuestionPaper: boolean;
  shouldUploadAnswerSheet: boolean;
}) => {
  //If we should upload none of the two, then just dont even make the API request
  if (!shouldUploadQuestionPaper && !shouldUploadAnswerSheet) return null;

  const request = await useHttpRequest<{
    success: boolean;
    message: string;
    question_paper: { upload_url: AwsUploadUrl; path_key: string };
    answer_sheet: { upload_url: AwsUploadUrl; path_key: string } | null;
  }>({
    method: "POST",
    url: "course-work/request-for-file-upload-urls",
    loadingProperty: null,
    showSuccessMessage: false,
    body: {
      course_work_id: route.params.courseWorkId,
      ...(shouldUploadQuestionPaper && {
        question_paper: {
          file_name: selectedQuestionFile.value?.raw?.name,
          file_size: selectedQuestionFile.value?.raw?.size,
          mime_type: selectedQuestionFile.value?.raw?.type,
        },
      }),
      ...(shouldUploadAnswerSheet && {
        answer_sheet: {
          file_name: selectedAnswerFile.value?.raw?.name,
          file_size: selectedAnswerFile.value?.raw?.size,
        },
      }),
    },
  });

  if (!request) return;
  if (request.success && request.message == "UPLOAD URLS GENERATED") {
    return {
      question_paper: request.question_paper,
      answer_sheet: request.answer_sheet,
    };
  }
};

const uploadResourceToAWS = async ({
  awsUploadUrl,
  selectedFile,
  onProgress,
}: {
  awsUploadUrl: AwsUploadUrl;
  selectedFile: File;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onProgress: (progressEvent: ProgressEvent) => void;
}) => {
  try {
    const formData = new FormData();
    Object.entries(awsUploadUrl.fields).forEach(([key, value]) => {
      //@ts-ignore
      formData.append(key, value);
    });
    formData.append("file", selectedFile);

    // send the file to the aws server
    const response = await axios.post(`${awsUploadUrl.url}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: " ",
      },
      onUploadProgress: onProgress,
    });

    if (response.status == 204) {
      return response.headers.location;
    } else {
      throw "UNEXPECTED_RESPONSE";
    }
  } catch (error: any) {
    if (error.message == "Network Error") {
      useShowWarningMessage(
        "Connection failed",
        "A network error occurred, please try again."
      );
    }
    useShowWarningMessage(
      "Upload Failed",
      "Unable to Upload Resource Now, please try again"
    );
  }
};

const validateInput = () => {
  if (!chosenCourseUnitsWithIntakes.value.length) {
    return useShowWarningMessage(
      "Modules Required",
      "Modules for this Course Work are required"
    );
  }

  if (!courseworkTitle.value) {
    return useShowWarningMessage(
      "Course Work Required",
      "The Title for this Course Work are required"
    );
  }
  if (courseworkTitle.value.length > 255) {
    return useShowWarningMessage(
      "Course Work too long",
      "The Title for this Course Work is too long. It should be less than 255 Characters"
    );
  }

  if (!totalMarks.value) {
    return useShowWarningMessage(
      "Course Work Total Marks Required",
      "The Total Marks for this course work is required"
    );
  }
  if (
    totalMarks.value &&
    (isNaN(+totalMarks.value) ||
      +totalMarks.value > 100 ||
      +totalMarks.value <= 0)
  ) {
    return useShowWarningMessage(
      "Total Marks Invalid",
      "When supplied, total marks should be a number between 0 and 100"
    );
  }

  if (!startTime.value || !endTime.value) {
    return useShowWarningMessage(
      "Start & End Time Required",
      "The Start & End Time are both Required "
    );
  }

  if (+startTime.value >= +endTime.value) {
    return useShowWarningMessage(
      "Invalid Time",
      "The End Time can not be before or equal to the Start Time, please review it and try again."
    );
  }

  if (
    courseworkInstructions.value &&
    courseworkInstructions.value.length > 1000
  ) {
    return useShowWarningMessage(
      "Instructions too Long",
      "Course Work Instructions are too long. It should be less than 1000 Characters"
    );
  }

  //Ensure the course work question file array is not empty
  if (!courseworkQuestionFiles.value.length) {
    return useShowWarningMessage(
      "Question Paper Required",
      "The Question Paper File is required"
    );
  }

  if (
    selectedQuestionFile.value &&
    (selectedQuestionFile.value?.raw?.size ?? 0) > 100 * 1024 * 1024
  ) {
    return useShowWarningMessage(
      "File Too Big",
      "The Question Paper File you are trying to upload is too Big. Please choose another one below 100Mbs."
    );
  }

  //If answer sheet is supplied, Ensure it is not more than 100Mbs
  if (
    selectedAnswerFile.value &&
    (selectedAnswerFile.value?.size ?? 0) > 100 * 1024 * 1024
  ) {
    return useShowWarningMessage(
      "File Too Big",
      "The Answer Sheet File you are trying to upload is too Big. Please choose another one below 100Mbs."
    );
  }
  return true;
};

const onEditCoursework = async () => {
  let loadingIndicator;
  try {
    if (validateInput() !== true) {
      return;
    }

    loadingIndicator = Loading.service({
      lock: true,
      text: "Please wait...",
      background: "rgba(0, 0, 0, 0.7)",
    });

    let uploadUrls: Awaited<ReturnType<typeof requestUploadUrls>> | null;

    if (selectedQuestionFile.value || selectedAnswerFile.value) {
      uploadUrls = await requestUploadUrls({
        shouldUploadQuestionPaper: !!selectedQuestionFile.value,
        shouldUploadAnswerSheet: !!selectedAnswerFile.value,
      });

      if (!uploadUrls) return;
    }
    //If there's any upload URL requested, then upload any
    let questionPaperPathUrl, answerSheetPathUrl;
    if (uploadUrls) {
      if (uploadUrls.question_paper) {
        questionPaperPathUrl = await uploadResourceToAWS({
          awsUploadUrl: uploadUrls.question_paper.upload_url,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          selectedFile: selectedQuestionFile.value?.raw!,
          onProgress: (progressEvent: ProgressEvent) => {
            const loader = document.querySelector(
              ".el-loading-text"
            ) as HTMLElement;

            const progress = +(
              (progressEvent.loaded / progressEvent.total) *
              100
            ).toFixed(0);

            loader.innerText = `Uploading Question Paper... ${progress}%`;
          },
        });
      }

      if (uploadUrls.answer_sheet) {
        //If the user chose an answer sheet too, upload it
        answerSheetPathUrl = await uploadResourceToAWS({
          awsUploadUrl: uploadUrls.answer_sheet?.upload_url,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          selectedFile: selectedAnswerFile.value?.raw!,
          onProgress: (progressEvent: ProgressEvent) => {
            const loader = document.querySelector(
              ".el-loading-text"
            ) as HTMLElement;

            const progress = +(
              (progressEvent.loaded / progressEvent.total) *
              100
            ).toFixed(0);

            loader.innerText = `Uploading Answer sheet... ${progress}%`;
          },
        });
      }
    }

    const request = await useHttpRequest<{ success: boolean; message: string }>(
      {
        method: "PATCH",
        url: `course-work/edit/${route.params.courseWorkId}`,
        loadingProperty: null,
        showSuccessMessage: true,
        body: {
          title: courseworkTitle.value,
          total_marks: totalMarks.value,
          question_paper_path_url: selectedQuestionFile.value
            ? questionPaperPathUrl
            : undefined,
          question_paper_path_key: selectedQuestionFile.value
            ? uploadUrls?.question_paper?.path_key
            : undefined,
          answer_sheet_path_url: selectedAnswerFile.value
            ? answerSheetPathUrl
            : undefined,
          answer_sheet_path_key: uploadUrls?.answer_sheet
            ? uploadUrls?.answer_sheet.path_key
            : undefined,
          available_from: startTime.value,
          available_upto: endTime.value,
          instructions: courseworkInstructions.value,
          question_paper_mime_type: selectedQuestionFile.value?.raw?.type,
          course_units_and_batches: chosenCourseUnitsWithIntakes.value.map(
            (item) => {
              /* Since the Id is intentionally concatenated with the batch and course unit separated by an underscore
                since a course work can be on the same intake but multiple course units, it causes vue v-for duplication errors
                but the combination of the intake and course unit is always unique
              we split it before we send to the API */
              const itemParts = item.split("_");
              return {
                course_intake_batch_id: itemParts[0],
                course_unit_id: itemParts[1],
              };
            }
          ),
          intake_group_ids: chosenIntakeGroups.value,
          //If the user removed the answer sheet and did not choose another one, means they are now removing an answer sheet that
          //probably they had accidentally added before. So we gotta tell the Backend that the user has removed the answer sheet
          //intentionally such that it does not misinterpret it's absence as no change..
          should_remove_answer_sheet: answerSheetsFiles.value.length == 0,
          is_answersheet_file_upload_allowed: isFileUploadAllowed.value,
        },
      }
    );

    if (!request) return;
    if (
      request.success &&
      request.message == "Course Work edited successfully"
    ) {
      router.back();
    }
  } catch (error) {
    useShowWarningMessage("Error", "An error occurred while uploading files");
  } finally {
    loadingIndicator?.close();
  }
};

return { __sfc: true,route, router, isSearching, totalMarks, courseworkTitle, courseworkInstructions, startTime, endTime, isFileUploadAllowed, chosenCourseUnitsWithIntakes, chosenIntakeGroups, courseUnitsWithCourseIntakeBatches, intakeGRoupSearchResults, courseworkQuestionFiles, answerSheetsFiles, selectedAnswerFile, selectedQuestionFile, getCourseWork, onSearchModules, onSearchIntakeGroups, handleQuestionPreview, handleQuestionRemove, handleAnswerPreview, handleAnswerRemove, requestUploadUrls, uploadResourceToAWS, validateInput, onEditCoursework, BreadCrumbComponent }
}

})